import './style.scss';
import {
  TextInput,
  useToastContext,
  Button,
  TwitterIcon,
  FacebookIcon,
  LinkedinIcon,
  YoutubeIcon,
  HorizontalLine,
  emailRules,
  nameRules,
  required,
  Loading,
  useForm,
  TruForm,
  urlRule,
  FormArray,
  SelectWithFilter,
  Option,
} from '@trufla-technology/trumarket-react-design-system';
import {
  useListCompanyContacts,
  useGetCompany,
  useUpdateCompanyContact,
  useUpdateCompanyPhone,
  useUpdateCompany,
  useListCompanyPhones,
  useCreateCompanyPhone,
  useCreateCompanyContact,
  useDeleteCompanyPhone,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { IErrorResponse } from 'app/types/ErrorResponse';
import { useEffect, useState, useRef } from 'react';
import { LocalizationSelect } from 'app/components/LocalizationSelect';
import { CurrencySelect } from 'app/components/CurrencySelect';
import { capitalize, cloneDeep, isEqual } from 'lodash';
import { useAuthContext } from '@trufla-technology/trufla-trudomain/dist/lib';
import { customPhoneRules } from '../BranchForm/phoneValidation';

export type SearchObject = {
  searchValue: string;
};

type IForm = {
  name: string;
  companySocialMedia: boolean;
  mailboxes: { mail: string; type: 'communication' | 'support' }[];
  phones:
    | { number: string | undefined; type: string | undefined }[]
    | undefined;
  businessType: string;
  'primaryContact.name': string;
  'primaryContact.phones': {
    type?: string | undefined;
    number?: string | undefined;
  }[];
  'primaryContact.title': string;
  'primaryContact.email': string;
  'socialMedia.facebook'?: string;
  'socialMedia.twitter'?: string;
  'socialMedia.linkedIn'?: string;
  'socialMedia.youtube'?: string;
  'localization.currency'?: string;
  'localization.locale'?: string;
  'localization.datetimeFormat'?: string;
  website?: string | undefined;
};

let [regex, min, max] = nameRules;
regex = { ...regex, pattern: /^[a-zA-Z0-9@!._,'\\-\s]*$/ };
const companyNameRules = [regex, min, max];

interface formItem {
  phoneNumber: string;
  type: string;
  key: string;
  status: string;
  index: number;
}
let updatedFormData: Array<formItem> = [];

export const CompanyDetailsForm = () => {
  const { data: CompanyData, loading } = useGetCompany();
  const {
    data: ListCompanyContacts,
    loading: loadingCompanyContacts,
    get: getCompanySingleContact,
  } = useListCompanyContacts({
    filter: {
      companyContact: true,
    },
  });
  let {
    data: CompanyPhones,
    loading: loadingCompanyPhones,
    get,
  } = useListCompanyPhones({
    filter: {
      companyPhone: true,
    },
  });

  const UpdateCompany = useUpdateCompany();
  const UpdateCompanyContact = useUpdateCompanyContact();
  const UpdateCompanyPhone = useUpdateCompanyPhone();
  const toaster = useToastContext();
  const CreateCompanyPhone = useCreateCompanyPhone();
  const CreateCompanyContact = useCreateCompanyContact();
  const DeleteCompanyPhone = useDeleteCompanyPhone();
  const [buttonState, setButtonState] = useState(true);
  const initialFormData = useRef({});
  const [phoneNotDeleted, setPhoneNotDeleted] = useState(true);
  const maxLengthUrlRule = [...urlRule, { max: 130 }];

  let keyToBeDelete = '';

  const form = useForm<IForm>(
    CompanyData
      ? {
          ...CompanyData,

          phones:
            CompanyPhones && CompanyPhones?.length > 0
              ? CompanyPhones?.map((phone) => {
                  return { number: phone.number, type: phone.type };
                })
              : [{ number: '', type: '' }],
          'localization.currency': CompanyData.localization?.currency,
          'localization.locale': CompanyData.localization?.locale,
          'localization.datetimeFormat':
            CompanyData.localization?.datetimeFormat,
          'socialMedia.facebook': CompanyData.socialMedia?.facebook,
          'socialMedia.twitter': CompanyData.socialMedia?.twitter,
          'socialMedia.linkedIn': CompanyData.socialMedia?.linkedIn,
          'socialMedia.youtube': CompanyData.socialMedia?.youtube,
          website: CompanyData.website,
        }
      : {},
  );
  const { values, update } = form;

  useEffect(() => {
    const initialize = async () => {
      const formValues = values();
      if (ListCompanyContacts && ListCompanyContacts[0]) {
        update({
          ...formValues,
          'primaryContact.name': ListCompanyContacts[0].name,
          'primaryContact.phones': ListCompanyContacts[0].phones?.map((ph) => {
            return {
              ...ph,
              type: capitalize(ph.type),
            };
          }),
          'primaryContact.title': ListCompanyContacts[0].title,
          'primaryContact.email': ListCompanyContacts[0].email,
        });

        if (!isEqual(values(), {})) {
          initialFormData.current = cloneDeep(values());
        }
      } else {
        update({
          ...formValues,
          'primaryContact.name': undefined,
          'primaryContact.phones': [{ number: undefined, type: undefined }],
          'primaryContact.title': undefined,
          'primaryContact.email': undefined,
        });
      }
    };
    initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ListCompanyContacts, CompanyData, CompanyPhones, values, update]);

  const handleChange = () => {
    if (phoneNotDeleted) {
      let socialMdiaValues = values();
      socialMdiaValues['socialMedia.youtube'] = !!!socialMdiaValues[
        'socialMedia.youtube'
      ]
        ? undefined
        : socialMdiaValues['socialMedia.youtube'];
      socialMdiaValues['socialMedia.facebook'] = !!!socialMdiaValues[
        'socialMedia.facebook'
      ]
        ? undefined
        : socialMdiaValues['socialMedia.facebook'];
      socialMdiaValues['socialMedia.twitter'] = !!!socialMdiaValues[
        'socialMedia.twitter'
      ]
        ? undefined
        : socialMdiaValues['socialMedia.twitter'];
      socialMdiaValues['socialMedia.linkedIn'] = !!!socialMdiaValues[
        'socialMedia.linkedIn'
      ]
        ? undefined
        : socialMdiaValues['socialMedia.linkedIn'];

      setButtonState(isEqual(socialMdiaValues, initialFormData.current));
    } else {
      initialFormData.current = {};
      setButtonState(false);
    }
  };
  const handleFinish = async (formData: Partial<IForm>) => {
    const socialMediaKeys: Array<keyof Partial<IForm>> = [
      'socialMedia.facebook',
      'socialMedia.twitter',
      'socialMedia.linkedIn',
      'socialMedia.youtube',
    ];

    const hasSocialMedia = socialMediaKeys.some((key) => formData[key]);

    const constructSocialMedia = () =>
      hasSocialMedia
        ? {
            socialMedia: {
              facebook: formData['socialMedia.facebook'],
              twitter: formData['socialMedia.twitter'],
              linkedIn: formData['socialMedia.linkedIn'],
              youtube: formData['socialMedia.youtube'],
            },
          }
        : {};

    const companyDetails = {
      name: formData.name,
      businessType: formData.businessType,
      mailboxes: formData.mailboxes,
      website: formData.website,
      ...constructSocialMedia(),
      localization: {
        currency: formData['localization.currency'],
        locale: formData['localization.locale'],
        datetimeFormat: formData['localization.datetimeFormat'],
      },
    };

    const constructPrimaryContactDetails = () => ({
      name: formData['primaryContact.name'],
      email: formData['primaryContact.email'],
      title: formData['primaryContact.title'],
      //@ts-ignore - until BE supports phone types
      phones: formData['primaryContact.phones']?.map((phone) => ({
        ...phone,
        number: `${phone.number}`,
      })),
    });

    const managePhones = async () => {
      if (updatedFormData.length && CompanyData) {
        await Promise.all(
          updatedFormData.map(
            async ({ key }) => key && DeleteCompanyPhone(key),
          ),
        );

        const filteredKeys = updatedFormData.map(({ key }) => key);
        CompanyPhones = CompanyPhones?.filter(
          //@ts-ignore
          (phone) => !filteredKeys.includes(phone.key),
        );
        updatedFormData = [];
      }

      await Promise.all(
        formData.phones?.map(async (phone, index) => {
          const companyPhone = CompanyPhones?.[index];
          if (companyPhone?.key) {
            return UpdateCompanyPhone(companyPhone.key, {
              number: `${phone.number}`,
              //@ts-ignore - until BE supports phone types
              type: capitalize(phone.type),
            });
          } else {
            return CreateCompanyPhone({
              number: `${phone.number}`,
              //@ts-ignore - until BE supports phone types
              type: capitalize(phone.type!),
            });
          }
        }) || [],
      );
    };

    try {
      //@ts-ignore
      const updateCompanyCall = UpdateCompany(companyDetails);

      const updateOrCreateContactCall = ListCompanyContacts?.[0]?.key
        ? UpdateCompanyContact(
            ListCompanyContacts[0].key,
            //@ts-ignore
            constructPrimaryContactDetails(),
          )
        : //@ts-ignore
          CreateCompanyContact(constructPrimaryContactDetails());

      await managePhones();

      await Promise.all([updateOrCreateContactCall, updateCompanyCall]);

      toaster.success('Successfully changed!');
      await Promise.all([getCompanySingleContact(), get()]);
      setButtonState(true);
    } catch (error) {
      //@ts-ignore
      const errorMessage =
        (error as IErrorResponse)?.response?.data?.error?.replaceAll(
          'body.',
          '',
        ) || 'Failed to edit company details';
      toaster.error(errorMessage);
    }
  };

  const onDeletePhone = (key: number) => {
    if (CompanyPhones && key) {
      keyToBeDelete = CompanyPhones[key]?.key as string;
      const phoneNumber = CompanyPhones[key]?.number as string;
      const phoneType = CompanyPhones[key]?.type as string;
      const phoneIndex = key;

      updatedFormData.push({
        phoneNumber: phoneNumber,
        type: phoneType,
        key: keyToBeDelete,
        status: 'deleted',
        index: phoneIndex,
      });
      setPhoneNotDeleted(false);
      handleChange();
    }
  };

  const optionList = ['Phone', 'Fax', 'Cell'];
  const dateFormatOptions = [
    'YYYY-MM-DD',
    'MM/DD/YYYY',
    'DD/MM/YYYY',
    'DD.MM.YYYY',
    'MM/DD/YYYY hh:mm A',
    'DD/MM/YYYY HH:mm',
    'YYYY-MM-DD HH:mm:ss',
    'MM/DD/YYYY hh:mm:ss A',
    'DD/MM/YYYY HH:mm:ss',
  ];
  const { isPermitted } = useAuthContext();

  return (
    <div className="company-form-page">
      {loading && <Loading />}
      {!loading &&
        !loadingCompanyContacts &&
        !loadingCompanyPhones &&
        CompanyData && (
          <div className="form">
            <TruForm
              onFinish={handleFinish}
              form={form}
              onUpdate={handleChange}
              disabled={!isPermitted('updateCompany')}
            >
              <h2>Company</h2>
              <div className="company-details">
                <p className="title">Basic Details</p>
                <div className="inputs-wrraper">
                  <form.Item
                    name="name"
                    label="Company Name"
                    rules={[...companyNameRules, required]}
                  >
                    <TextInput />
                  </form.Item>

                  <FormArray
                    Form={form}
                    buttonType={'button'}
                    name="phones"
                    typeLabel="Phone"
                    fieldsLimit={4}
                    rules={customPhoneRules}
                    onDelete={onDeletePhone}
                    optionList={optionList}
                  ></FormArray>

                  <div className="website-section">
                    <div className="">
                      <form.Item
                        name="website"
                        label="Website"
                        rules={[required, ...urlRule]}
                      >
                        <TextInput />
                      </form.Item>
                    </div>
                  </div>
                  <form.Item
                    name="businessType"
                    label="Business Type"
                    rules={[required]}
                  >
                    <SelectWithFilter showSearch={false}>
                      <Option value={'Insurance'}>Insurance</Option>
                      <Option value={'E-Commerce'}>E-Commerce</Option>
                    </SelectWithFilter>
                  </form.Item>
                </div>
                <p className="title">Primary Contact </p>
                <div className="inputs-wrraper">
                  <form.Item
                    name="primaryContact.name"
                    label="Name"
                    rules={[required, ...nameRules]}
                  >
                    <TextInput />
                  </form.Item>
                  <form.Item
                    name="primaryContact.title"
                    label="Title"
                    rules={[required, ...nameRules]}
                  >
                    <TextInput />
                  </form.Item>

                  <FormArray
                    name="primaryContact.phones"
                    typeLabel="Phone"
                    fieldsLimit={4}
                    Form={form}
                    rules={customPhoneRules}
                    onDelete={() => {
                      setPhoneNotDeleted(false);
                      handleChange();
                    }}
                    optionList={optionList}
                  />

                  <form.Item
                    name="primaryContact.email"
                    label="Email"
                    rules={[required, ...emailRules]}
                  >
                    <TextInput />
                  </form.Item>
                </div>
                <p className="title">Social Media</p>
                <div className="inputs-wrraper">
                  <div className="flex-50">
                    <div className="socialmedia-input">
                      <div className="icon">
                        <form.Item>
                          <FacebookIcon width="10px" />
                        </form.Item>
                      </div>
                      <form.Item
                        name="socialMedia.facebook"
                        rules={maxLengthUrlRule}
                      >
                        <TextInput />
                      </form.Item>
                    </div>
                    <div className="socialmedia-input">
                      <div className="icon">
                        <form.Item>
                          <TwitterIcon width="18px" />
                        </form.Item>
                      </div>
                      <form.Item
                        name="socialMedia.twitter"
                        rules={maxLengthUrlRule}
                      >
                        <TextInput />
                      </form.Item>
                    </div>
                    <div className="socialmedia-input">
                      <div className="icon">
                        <form.Item>
                          <LinkedinIcon width="18px" />
                        </form.Item>
                      </div>
                      <form.Item
                        name="socialMedia.linkedIn"
                        rules={maxLengthUrlRule}
                      >
                        <TextInput />
                      </form.Item>
                    </div>
                    <div className="socialmedia-input">
                      <div className="icon">
                        <form.Item>
                          <YoutubeIcon width="18px" />
                        </form.Item>
                      </div>
                      <form.Item
                        name="socialMedia.youtube"
                        rules={maxLengthUrlRule}
                      >
                        <TextInput />
                      </form.Item>
                    </div>
                  </div>
                </div>
                <p className="title">Localization</p>
                <div className="inputs-wrraper">
                  <form.Item
                    name="localization.locale"
                    label="Default Language"
                    rules={[required]}
                  >
                    <LocalizationSelect />
                  </form.Item>
                  <form.Item
                    name="localization.currency"
                    label="Used Currency"
                    rules={[required]}
                  >
                    <CurrencySelect />
                  </form.Item>
                  <span className="date">
                    <form.Item
                      name="localization.datetimeFormat"
                      label="Date / Time  Format"
                      rules={[required]}
                    >
                      <SelectWithFilter showSearch={false}>
                        {dateFormatOptions.map((format) => (
                          <Option key={format} value={format}>
                            {format}
                          </Option>
                        ))}
                      </SelectWithFilter>
                    </form.Item>
                  </span>
                </div>
              </div>
              <HorizontalLine verticalMargin="25px" />
              <Button
                disabled={buttonState || !isPermitted('updateCompany')}
                type="primary"
                htmlType="submit"
              >
                Save
              </Button>
            </TruForm>
          </div>
        )}
    </div>
  );
};
